// 캔버스로 그리는 텍스처 도우미.
import * as THREE from 'three';

type Draw = (x: CanvasRenderingContext2D, w: number, h: number) => void;

/** w×h 캔버스에 그려 텍스처로. repeat를 주면 반복 감기(RepeatWrapping)로 설정한다. */
export function canvasTex(w: number, h: number, draw: Draw, repeat?: { x?: number; y?: number }): THREE.CanvasTexture {
  const c = document.createElement('canvas');
  c.width = w;
  c.height = h;
  draw(c.getContext('2d')!, w, h);
  const t = new THREE.CanvasTexture(c);
  if (repeat) {
    t.wrapS = t.wrapT = THREE.RepeatWrapping;
    t.repeat.set(repeat.x || 1, repeat.y || 1);
  }
  return t;
}

let steam: THREE.DataTexture | null = null;

/** 흰 원형 그라데이션 공유 텍스처 (김·안개·입김·눈송이 스프라이트). 프로토타입 steamTex. */
export const steamTex = (): THREE.DataTexture => (steam ||= radialTex('255,255,255'));

let halo: THREE.DataTexture | null = null;
/** 따뜻한 빛 후광 공유 텍스처 (라군 가로등, 서울 야경). 프로토타입 haloTex. */
export const haloTex = (): THREE.DataTexture => (halo ||= radialTex('255,220,160'));

/**
 * 가운데가 진하고 가장자리로 투명해지는 원형 그라데이션 (김, 후광 스프라이트용). rgb는 '255,255,255' 형식.
 * 캔버스로 그리지 않고 픽셀 값을 직접 만든다: 아이폰 사파리는 투명에 가까운 캔버스 픽셀의 색을 제멋대로 남겨,
 * 김이 겹친 곳에 색색의 점이 보였다. 색은 어디나 같고 투명도만 캔버스 radialGradient와 같은 직선 곡선(1 - r/R).
 */
export function radialTex(rgb: string, size = 64): THREE.DataTexture {
  const [r, g, b] = rgb.split(',').map(Number) as [number, number, number];
  const data = new Uint8Array(size * size * 4), R = size / 2;
  for (let j = 0; j < size; j++) for (let i = 0; i < size; i++) {
    const d = Math.hypot(i + 0.5 - R, j + 0.5 - R) / R, k = (j * size + i) * 4;
    data[k] = r; data[k + 1] = g; data[k + 2] = b;
    data[k + 3] = Math.round(255 * Math.max(0, 1 - d));
  }
  const t = new THREE.DataTexture(data, size, size, THREE.RGBAFormat);
  // CanvasTexture와 같은 필터·밉맵 (DataTexture 기본값은 Nearest·밉맵 없음)
  t.magFilter = THREE.LinearFilter;
  t.minFilter = THREE.LinearMipmapLinearFilter;
  t.generateMipmaps = true;
  t.needsUpdate = true;
  return t;
}

/**
 * 캔버스에 그린 모양의 투명도만 쓰고 색은 rgb 하나로 고정한 텍스처 (반짝임처럼 흰 반투명 모양).
 * 아이폰 사파리에서 투명에 가까운 픽셀 색이 흐트러지지 않게 한다 (radialTex와 같은 이유).
 */
export function alphaTex(w: number, h: number, draw: Draw, rgb = '255,255,255'): THREE.DataTexture {
  const c = document.createElement('canvas');
  c.width = w; c.height = h;
  const g = c.getContext('2d')!;
  draw(g, w, h);
  const src = g.getImageData(0, 0, w, h).data, [r, gg, b] = rgb.split(',').map(Number) as [number, number, number];
  const data = new Uint8Array(w * h * 4);
  // 캔버스는 위가 0행, 텍스처는 아래가 0행이라 뒤집어 담는다 (CanvasTexture의 flipY와 같게)
  for (let j = 0; j < h; j++) for (let i = 0; i < w; i++) {
    const s = ((h - 1 - j) * w + i) * 4, k = (j * w + i) * 4;
    data[k] = r; data[k + 1] = gg; data[k + 2] = b; data[k + 3] = src[s + 3]!;
  }
  const t = new THREE.DataTexture(data, w, h, THREE.RGBAFormat);
  t.magFilter = THREE.LinearFilter;
  t.minFilter = THREE.LinearMipmapLinearFilter;
  t.generateMipmaps = true;
  t.needsUpdate = true;
  return t;
}
